import { useFrame } from "@react-three/fiber";
import { useEffect, useRef, type RefObject } from "react";
import { Object3D, Quaternion, Vector3 } from "three";

export const useFaceUp = (
  enabled: boolean,
  object: RefObject<Object3D>,
  preview?: boolean,
  randomRotation?: boolean,
) => {
  const lastParentQuaternionRef = useRef(new Quaternion());
  const parentQuaternionRef = useRef(new Quaternion());

  // We want to randomize once per die instead of every render
  const rotationRef = useRef<number>(0);
  useEffect(() => {
    rotationRef.current = Math.random() * Math.PI * 2;
  }, []);

  const upQuaternion = new Quaternion().setFromUnitVectors(
    new Vector3(0, 0, -1), // represents threejs forward default direction
    new Vector3(0, preview ? 0 : 1, preview ? -1 : 0).normalize(),
  );

  useFrame(() => {
    if (!(enabled && object.current)) return;

    if (object.current.parent) {
      // Offset our desired rotation by the parent's rotation
      object.current.parent.updateWorldMatrix(true, false);
      object.current.parent.getWorldQuaternion(parentQuaternionRef.current);

      if (lastParentQuaternionRef.current.equals(parentQuaternionRef.current))
        return;
      lastParentQuaternionRef.current.copy(parentQuaternionRef.current);

      parentQuaternionRef.current.invert().multiply(upQuaternion);
      object.current.quaternion.copy(parentQuaternionRef.current);
    } else if (!object.current.quaternion.equals(upQuaternion)) {
      // No parent so set our desired rotation directly
      object.current.quaternion.copy(upQuaternion);
    }

    if (randomRotation) {
      // Rotate around the object's local Y axis (up after face-up orientation)
      object.current.rotateOnAxis(new Vector3(0, 1, 0), rotationRef.current);
    }
  });
};
